import { useQuery } from "@tanstack/react-query";
import { Filter, Highlighter, MessageSquare, Tag as TagIcon, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type Annotation, api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  docId: string;
}

type KindFilter = "all" | "highlight" | "comment" | "tag";

/**
 * C1.3 — Drawer "todas las anotaciones de este documento".
 *
 * Similar al Highlights Inbox cross-document pero filtrado al doc actual.
 * Lista todas las anotaciones (highlight + comment + tag) con filtros y
 * jump-to-block via #anchor.
 */
export function DocAnnotationsDrawer({ open, onClose, docId }: Props) {
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [colorFilter, setColorFilter] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["doc-annotations", docId],
    queryFn: () => api.listAnnotations(docId),
    enabled: open && !!docId,
    staleTime: 30_000,
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && open) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const items = data?.items ?? [];
  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (kindFilter !== "all" && a.kind !== kindFilter) return false;
      if (colorFilter && a.color !== colorFilter) return false;
      return true;
    });
  }, [items, kindFilter, colorFilter]);

  const counts = useMemo(
    () => ({
      all: items.length,
      highlight: items.filter((a) => a.kind === "highlight").length,
      comment: items.filter((a) => a.kind === "comment").length,
      tag: items.filter((a) => a.kind === "tag").length,
    }),
    [items],
  );

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        data-testid="doc-annotations-backdrop"
        className="fixed inset-0 z-30 bg-black/30"
      />
      <aside
        data-testid="doc-annotations-drawer"
        className="fixed right-0 top-0 z-40 flex h-full w-96 flex-col border-l border-(--border-default) bg-(--bg-canvas) shadow-(--elev-3)"
      >
        <header className="flex items-center justify-between border-b border-(--border-subtle) px-4 py-3">
          <div className="flex items-center gap-2">
            <Highlighter size={14} />
            <h3 className="text-sm font-semibold">Anotaciones del documento</h3>
            <span className="font-mono text-[10px] text-(--text-tertiary)">
              {filtered.length}/{items.length}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="doc-annotations-close"
            className="rounded p-1 text-(--text-tertiary) hover:bg-(--bg-hover)"
          >
            <X size={14} />
          </button>
        </header>

        <div
          className="flex flex-wrap items-center gap-1.5 border-b border-(--border-subtle) px-3 py-2 text-[11px]"
          data-testid="doc-annotations-filters"
        >
          <Filter size={11} className="text-(--text-tertiary)" />
          {(
            [
              { id: "all" as KindFilter, label: "Todas" },
              { id: "highlight" as KindFilter, label: "Subrayados" },
              { id: "comment" as KindFilter, label: "Comentarios" },
              { id: "tag" as KindFilter, label: "Etiquetas" },
            ] as const
          ).map((opt) => (
            <button
              type="button"
              key={opt.id}
              onClick={() => setKindFilter(opt.id)}
              data-testid={`doc-annotations-filter-${opt.id}`}
              data-active={kindFilter === opt.id ? "true" : "false"}
              className={`rounded-full px-2.5 py-0.5 transition ${
                kindFilter === opt.id
                  ? "bg-(--accent-500) text-white"
                  : "bg-(--bg-elevated) text-(--text-secondary) hover:bg-(--bg-hover)"
              }`}
            >
              {opt.label} <span className="font-mono opacity-70">{counts[opt.id]}</span>
            </button>
          ))}
          {kindFilter === "highlight" && (
            <span className="ml-1 inline-flex items-center gap-1">
              {(["yellow", "green", "pink", "blue", "purple"] as const).map((c) => (
                <button
                  type="button"
                  key={c}
                  aria-label={`Color ${c}`}
                  data-testid={`doc-annotations-color-${c}`}
                  onClick={() => setColorFilter(colorFilter === c ? null : c)}
                  className={`h-3.5 w-3.5 rounded-full border ${
                    colorFilter === c
                      ? "border-(--text-primary) ring-1 ring-(--text-primary)"
                      : "border-(--border-default)"
                  }`}
                  style={{ background: `var(--highlight-${c})` }}
                />
              ))}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {isLoading && <p className="text-xs text-(--text-tertiary)">Cargando…</p>}
          {!isLoading && filtered.length === 0 && (
            <p
              data-testid="doc-annotations-empty"
              className="rounded-md border border-dashed border-(--border-strong) p-4 text-center text-xs text-(--text-tertiary)"
            >
              Sin anotaciones para los filtros actuales.
            </p>
          )}
          <ul className="space-y-2">
            {filtered.map((a) => (
              <AnnotationRow key={a.id} annotation={a} />
            ))}
          </ul>
        </div>
      </aside>
    </>
  );
}

function AnnotationRow({ annotation: a }: { annotation: Annotation }) {
  const Icon = a.kind === "comment" ? MessageSquare : a.kind === "tag" ? TagIcon : Highlighter;
  const blockId = (() => {
    try {
      const r = JSON.parse(a.range_json) as { blockId?: string; quote?: string };
      return { blockId: r.blockId, quote: r.quote };
    } catch {
      return { blockId: undefined, quote: undefined };
    }
  })();
  return (
    <li
      data-testid="doc-annotation-row"
      data-kind={a.kind}
      className="rounded-md border border-(--border-subtle) bg-(--bg-elevated) p-2.5"
    >
      <header className="flex items-center gap-2 text-[10px] text-(--text-tertiary)">
        <Icon size={11} />
        <span className="font-mono uppercase tracking-wider">{a.kind}</span>
        {a.color && (
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: `var(--highlight-${a.color})` }}
            aria-label={`Color ${a.color}`}
          />
        )}
        <span className="ml-auto font-mono">{formatRelative(a.created_at)}</span>
      </header>
      {blockId.quote && (
        <blockquote className="mt-1.5 border-l-2 border-(--accent-300) pl-2 text-xs italic text-(--text-secondary)">
          "{blockId.quote}"
        </blockquote>
      )}
      {a.body && (
        <p className="mt-1.5 text-xs text-(--text-primary)" style={{ wordBreak: "break-word" }}>
          {a.kind === "tag" ? `#${a.body}` : a.body}
        </p>
      )}
      <footer className="mt-1.5 flex items-center justify-between text-[10px] text-(--text-tertiary)">
        <span>{a.author_subject}</span>
        {blockId.blockId && (
          <a
            href={`#${blockId.blockId}`}
            data-testid="doc-annotation-jump"
            className="font-mono text-(--accent-600) hover:underline"
          >
            ir al bloque →
          </a>
        )}
      </footer>
    </li>
  );
}
